/**
 * The one place the new Electrical module talks to storage — local, remote,
 * or both.
 *
 * Modeled on Csa/csaApi.js. Screens import this module and nothing else, so
 * that offline capture can be added (or changed) underneath without ever
 * touching a screen:
 *
 *   electricalApi.js         this facade — what the screens see
 *     └ offline/electricalRepo.js   decides local vs remote, owns the outbox
 *         ├ offline/electricalDb.js       IndexedDB
 *         ├ offline/outboxRules.js        the queueing rules (unit-testable)
 *         └ offline/net.js                HTTP
 *
 * Offline is opt-in per audit. Until an audit is provisioned, every call
 * here goes straight to the server exactly as it did before — so writing up
 * on a laptop behaves as it always has, and nothing accumulates a local
 * database nobody asked for. See electricalRepo.js for why.
 *
 * Backend note: beyond organizations/audits/people/schedule (already used by
 * ElectricalAuditList.js/ElectricalNewAudit.js) and /api/all-data (existing,
 * reused as-is), the /api/electrical/* endpoints this module calls don't
 * exist yet. See offline/net.js for the expected request/response shapes,
 * which mirror sql/01_elec_schema.sql's elec_* tables.
 */

import * as net from "./offline/net";
import * as repo from "./offline/electricalRepo";

export { ElectricalError } from "./offline/net";
export { newLocalId } from "./offline/outboxRules";

/* ------------------------------------------------------------------ */
/* clients — always online; you cannot start an audit from a plant floor */
/* ------------------------------------------------------------------ */

export const listOrganizations = net.listOrganizations;

/** The master checklist - area/category options on the new-audit form. */
export const getAllData = net.getAllData;

/* ------------------------------------------------------------------ */
/* the audit                                                           */
/* ------------------------------------------------------------------ */

export const listAudits = net.listAudits;
export const previewDiscard = net.previewDiscard;
export const discardAudit = net.discardAudit;
export const createAudit = net.createAudit;

export const getFramework = repo.getFramework;
export const getAudit = repo.getAudit;
export const clearFrameworkCache = net.clearFrameworkCache;

/** The new-audit form's area/category picker - see offline/net.js. */
export const getAreaCategory = net.getAreaCategory;

/**
 * Scores are the server's business - deliberately not computed locally (see
 * the CSA-parity analysis for why a second client implementation of scoring
 * would eventually disagree with the first). Offline, the capture screen
 * should show progress instead, which is the number that tells an auditor
 * what to do next.
 */
export const getRollup = net.getRollup;
export const getReadiness = net.getReadiness;

/* ------------------------------------------------------------------ */
/* capture — local-first once provisioned                              */
/* ------------------------------------------------------------------ */

export const saveAnswer = repo.saveAnswer;
export const bulkMarkCompliant = repo.bulkMarkCompliant;

export const addExclusion = repo.addExclusion;
export const removeExclusion = repo.removeExclusion;

export const listNotes = repo.listNotes;
export const addNote = repo.addNote;
export const useNote = repo.useNote;
export const deleteNote = repo.deleteNote;

export const listExtras = repo.listExtras;
export const addExtra = repo.addExtra;
export const updateExtra = repo.updateExtra;
export const deleteExtra = repo.deleteExtra;

export const getGaps = net.getGaps;

/* ------------------------------------------------------------------ */
/* carry-forward                                                       */
/* ------------------------------------------------------------------ */

export const carryPreview = net.carryPreview;
export const carryForward = net.carryForward;
export const confirmCarried = net.confirmCarried;
export const previousAnswers = net.previousAnswers;

/* ------------------------------------------------------------------ */
/* people / scheduling                                                 */
/* ------------------------------------------------------------------ */

export const listPeople = net.listPeople;
export const scheduleConflicts = net.scheduleConflicts;

/* ------------------------------------------------------------------ */
/* photos                                                               */
/* ------------------------------------------------------------------ */

export const capturePhoto = repo.capturePhoto;
export const listPhotos = repo.listPhotos;
export const linkPhoto = repo.linkPhoto;
export const unlinkPhoto = repo.unlinkPhoto;
export const deletePhoto = repo.deletePhoto;
export const uploadPhotos = repo.uploadPhotos;
export const unassignedPhotos = repo.unassignedPhotos;
export const storageEstimate = repo.storageEstimate;
export const syncAndFreeSpace = repo.syncAndFreeSpace;
export const photoBytes = net.photoBytes;

/* ------------------------------------------------------------------ */
/* workflow — online only (materialises the frozen scoring snapshot)   */
/* ------------------------------------------------------------------ */

export const submitAudit = net.submitAudit;
export const reviewAudit = net.reviewAudit;
export const returnAudit = net.returnAudit;
export const reopenAudit = net.reopenAudit;

/* ------------------------------------------------------------------ */
/* the report — composed server-side, online only                     */
/* ------------------------------------------------------------------ */

export const getReportModel = net.getReportModel;
export const getReportContents = net.getReportContents;
export const setReportContents = net.setReportContents;
export const saveReportSection = net.saveReportSection;
export const draftReportSection = net.draftReportSection;

export const openReportIssue = net.openReportIssue;
export const listReportIssues = net.listReportIssues;
export const getReportIssue = net.getReportIssue;

/** Drafting. Online only — drafting needs the server. */
export const startDraft = net.startDraft;
export const draftProgress = net.draftProgress;
export const cancelDraft = net.cancelDraft;
export const draftItems = net.draftItems;
export const acceptDraft = net.acceptDraft;

/* ------------------------------------------------------------------ */
/* offline                                                              */
/* ------------------------------------------------------------------ */

export const provision = repo.provision;
export const unprovision = repo.unprovision;
export const isProvisioned = repo.isProvisioned;

export const flush = repo.flush;
export const resync = repo.resync;
export const pending = repo.pending;
export const discard = repo.discard;

export const subscribeSync = repo.subscribe;
export const watchConnection = repo.watchConnection;
export const isOnline = repo.isOnline;
